import { z } from "zod";

import { gatewayOwnershipSchema, gatewayStatusSchema } from "./gateways";

/**
 * How far a device has got through being installed.
 *
 * The order is the procedure. Note what is missing from every request type in
 * this file: nothing sets COMMISSIONED. That status is written by the
 * ingestion path when an uplink actually arrives through one of the device's
 * configured gateways, because a sensor inside a metal-walled cold room
 * either reaches a gateway or it does not, and a tick in a form does not
 * change which (ADR-062).
 */
export const INSTALLATION_STATUSES = [
  /** Registered from the office. Nothing has happened on site yet. */
  "DRAFT",
  /** At least one gateway configured, waiting for the radio to prove it. */
  "AWAITING_UPLINK",
  /** An uplink arrived through a configured gateway. Verified, not asserted. */
  "COMMISSIONED",
  /** Abandoned. The device could not be made to report from where it is. */
  "FAILED",
] as const;

export const installationStatusSchema = z.enum(INSTALLATION_STATUSES);
export type InstallationStatus = z.infer<typeof installationStatusSchema>;

/**
 * What the caller's own organization can see about one gateway covering one of
 * its devices.
 *
 * Present on every entry regardless of who owns the gateway, because all of
 * it is the tenant's own data: they configured the link, and the signal is
 * their sensor's.
 */
const coverageBaseSchema = z.object({
  /**
   * The *link's* id, not the gateway's.
   *
   * Deliberate: the link belongs to the tenant even when the gateway does
   * not, so this is the handle a customer uses to remove coverage without
   * ever being given an identifier for shared infrastructure.
   */
  id: z.uuid(),
  linkedAt: z.iso.datetime(),
  linkedByName: z.string().nullable(),

  /**
   * When an uplink from this device was first received through this gateway.
   *
   * Null means this particular radio path has never carried a message. On a
   * device with several gateways that is the whole point of the field: a
   * redundant install where only one path has ever verified is not redundant,
   * and this is where that shows.
   */
  verifiedAt: z.iso.datetime().nullable(),
  verifiedRssi: z.number().int().nullable(),
  verifiedSnr: z.number().nullable(),
});

/** A gateway the caller's organization owns, or any gateway seen by a super admin. */
const identifiedCoverageSchema = coverageBaseSchema.extend({
  shared: z.literal(false),
  gatewayId: z.uuid(),
  eui: z.string(),
  name: z.string(),
  siteName: z.string().nullable(),
  status: gatewayStatusSchema,
  ownership: gatewayOwnershipSchema,
  lastSeenAt: z.iso.datetime().nullable(),
});

/**
 * Shared platform infrastructure, as a customer sees it.
 *
 * Everything identifying is absent, and so is the gateway's status and last
 * contact — those are derived from *other* tenants' traffic, and a customer
 * being able to watch a shared gateway go quiet is a customer being able to
 * infer something about the neighbours on it. What remains is the honest
 * answer to the only question a tenant has here: this device is covered by
 * platform infrastructure, and here is how well my own sensor reaches it
 * (ADR-062).
 */
const sharedCoverageSchema = coverageBaseSchema.extend({
  shared: z.literal(true),
});

export const deviceGatewaySchema = z.discriminatedUnion("shared", [
  identifiedCoverageSchema,
  sharedCoverageSchema,
]);

export type DeviceGateway = z.infer<typeof deviceGatewaySchema>;

/**
 * The two arms, exported separately because they are not interchangeable and
 * a `Partial` over the union would let a caller assemble a shape the API
 * cannot produce — a redacted entry with an EUI on it, for instance.
 */
export type IdentifiedCoverage = z.infer<typeof identifiedCoverageSchema>;
export type SharedCoverage = z.infer<typeof sharedCoverageSchema>;

export const deviceInstallationSchema = z.object({
  id: z.uuid(),
  deviceId: z.uuid(),
  organizationId: z.uuid(),
  status: installationStatusSchema,

  installedById: z.uuid().nullable(),
  installedByName: z.string().nullable(),

  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  failureReason: z.string().nullable(),
  notes: z.string().nullable(),

  /** Every gateway configured to cover the device, redacted where shared. */
  gateways: z.array(deviceGatewaySchema),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type DeviceInstallation = z.infer<typeof deviceInstallationSchema>;

/**
 * Not paginated, unlike every other list in the API. A device accumulates one
 * row per time somebody went out to install it, so the history is short
 * enough to send whole and a page control over it would be noise.
 */
export const deviceInstallationListSchema = z.array(deviceInstallationSchema);
export type DeviceInstallationList = z.infer<typeof deviceInstallationListSchema>;

export const openInstallationRequestSchema = z.object({
  /**
   * The platform user doing the work. Defaults to the caller, which is the
   * common case; naming someone else covers a supervisor recording a
   * technician's visit.
   */
  installedById: z.uuid().optional(),
  notes: z.string().trim().max(2_000).optional(),
});

export type OpenInstallationRequest = z.infer<typeof openInstallationRequestSchema>;

/**
 * Configures one or more gateways to cover the device.
 *
 * An array rather than a single id because covering one sensor with two
 * gateways is a deliberate redundancy decision, not an edge case. Linking is
 * idempotent: naming a gateway already linked leaves the existing link and
 * its verification evidence alone, so re-running this never discards proof
 * that a path once worked.
 */
export const linkGatewaysRequestSchema = z.object({
  gatewayIds: z.array(z.uuid()).min(1).max(10),
});

export type LinkGatewaysRequest = z.infer<typeof linkGatewaysRequestSchema>;

/**
 * Gives up on an installation.
 *
 * The counterpart to COMMISSIONED, and the only terminal state a person can
 * declare — because "this cannot be made to work from here" is a judgement
 * only somebody standing in the room can make.
 */
export const failInstallationRequestSchema = z.object({
  failureReason: z.string().trim().min(3).max(500),
});

export type FailInstallationRequest = z.infer<typeof failInstallationRequestSchema>;
